// https://pinia.esm.dev/introduction.html#basic-example
import config from '@/config'
import i18n from '@/i18'
import { defineStore } from 'pinia'

// interface Store {
//   appHandshake: number
//   retryCount: number
//   timezone: string
//   tokenId: string
//   changeRoute: boolean
//   userId: string
//   userType: string
//   userRole: string
//   companyId: string
//   countryCode: string
//   locationId: string
//   hasAffiliate: boolean
//   hasCampaign: boolean
//   skipOnboarding: boolean
//   hasStripeConnected: boolean
//   isStripeConnecting: boolean
// }
export const useAffiliateManagerStore = defineStore('affiliateManageStore', {
  state: () => {
    return {
      appHandshake: null as any,
      retryCount: 0,
      updateEmailTemplateTriggered: 0,
      tokenId: (import.meta.env.VITE_TOKEN_ID as string) || '',
      timezone: (import.meta.env.VITE_TIMEZONE as string) || '',
      userId: (import.meta.env.VITE_USER_ID as string) || '',
      userType: (import.meta.env.VITE_USER_TYPE as string) || '',
      userRole: (import.meta.env.VITE_USER_ROLE as string) || '',
      companyId: (import.meta.env.VITE_COMPANY_ID as string) || '',
      countryCode: (import.meta.env.VITE_COUNTRY_CODE as string) || '',
      locationId: (import.meta.env.VITE_LOCATION_ID as string) || '',
      hasAffiliate: (import.meta.env.VITE_HAS_AFFILIATE as boolean) || false,
      hasCampaign: (import.meta.env.VITE_HAS_CAMPAIGN as boolean) || false,
      hasLaunchedProgram:
        (import.meta.env.VITE_HAS_REACHED_LAUNCH_PROGRAM as boolean) || false,
      skipOnboarding:
        (import.meta.env.VITE_SKIP_ONBOARDING as boolean) || false,
      hasStripeConnected:
        (import.meta.env.VITE_HAS_STRIPE_CONNECTED as boolean) || false,
      isStripeConnecting:
        (import.meta.env.VITE_HAS_STRIPE_CONNECTED as boolean) || false,
      hasAuthorizeNetConnected:
        (import.meta.env.VITE_HAS_AUTHORIZE_NET_CONNECTED as boolean) || false,
      isAuthorizeNetConnecting:
        (import.meta.env.VITE_IS_AUTHORIZE_NET_CONNECTING as boolean) || false,
      hasPaypalConnected:
        (import.meta.env.VITE_HAS_PAYPAL_CONNECTED as boolean) || false,
      isPaypalConnecting:
        (import.meta.env.VITE_IS_PAYPAL_CONNECTING as boolean) || false,
      hasNmiConnected:
        (import.meta.env.VITE_HAS_NMI_CONNECTED as boolean) || false,
      isNmiConnecting:
        (import.meta.env.VITE_IS_NMI_CONNECTING as boolean) || false,
      changeRoute: false,
      customAffiliateName: undefined,
      customTitleName: undefined,
      customPayoutName: undefined,
      customCampaignName: undefined,
      customMediaName: undefined,
      showAddProduct: false,
      showRefresh: false,
      showFullScreen: false,
      builderPreviewDomain: undefined,
      showLevelUpItem: false,
      campaignCount: 0,
      currency: 'USD',
      embedUrl: '',
      customContactSingularLabel: i18n.global.t(
        'common.contact_singular_default'
      ) as string,
      customContactPluralLabel: i18n.global.t(
        'common.contact_plural_default'
      ) as string,
      isFirstPromoterImportEnabled: false,
    }
  },

  getters: {
    // Check if any payment gateway is connected
    hasAnyPaymentGatewayConnected: state => {
      return (
        state.hasStripeConnected ||
        state.hasAuthorizeNetConnected ||
        state.hasPaypalConnected ||
        state.hasNmiConnected
      )
    },
  },

  actions: {
    async init(payload: any, handshake: any) {
      this.setAppInitData(payload)
      this.setAppHandshake(handshake)
    },

    setAppInitData(payload: any) {
      this.userId = payload.userId
      this.userRole = payload.userRole
      this.userType = payload.userType
      this.companyId = payload.companyId
      this.countryCode = payload.countryCode
      this.locationId = payload.locationId
      this.hasAffiliate = payload.hasAffiliate
      this.hasCampaign = payload.hasCampaign
      this.hasLaunchedProgram = payload.hasLaunchedProgram
      this.hasStripeConnected = payload.hasStripeConnected
      this.hasAuthorizeNetConnected = payload.hasAuthorizeNetConnected
      this.hasPaypalConnected = payload.hasPaypalConnected
      this.hasNmiConnected = payload.hasNmiConnected
      this.skipOnboarding = payload.skipOnboarding
      this.tokenId = payload.token
      this.timezone = payload.timezone
      this.customAffiliateName = payload.customAffiliateName
      this.customTitleName = payload.customTitleName
      this.customPayoutName = payload.customPayoutName
      this.customCampaignName = payload.customCampaignName
      this.customMediaName = payload.customMediaName
      this.currency = payload?.locationCurrency || 'USD'
      this.showLevelUpItem = payload?.isLevelUpFeatureEnable || false
      this.embedUrl = payload?.embedUrl
        ? `${payload?.embedUrl}/am.js`
        : config.externalScriptFallbackURL

      if (payload?.customContactSingularLabel) {
        this.customContactSingularLabel = payload.customContactSingularLabel
      }
      if (payload?.customContactPluralLabel) {
        this.customContactPluralLabel = payload.customContactPluralLabel
      }

      if (
        payload?.isFirstPromoterImportEnabled ||
        config.levelUpEnabledLocations.includes(payload.locationId)
      ) {
        this.isFirstPromoterImportEnabled = true
      }
    },
    setAppHandshake(payload: any) {
      this.appHandshake = payload
    },
    refreshToken(newToken: string) {
      this.tokenId = newToken
    },
    setChangeRouteFlag(flag: boolean) {
      this.changeRoute = flag
    },
    updateRetryCount(count: number) {
      this.retryCount = count
    },
    updateStripeConnecting(value: boolean) {
      this.isStripeConnecting = value
    },
    updateAuthorizeNetConnecting(value: boolean) {
      this.isAuthorizeNetConnecting = value
    },
    updatePaypalConnecting(value: boolean) {
      this.isPaypalConnecting = value
    },
    updateNmiConnecting(value: boolean) {
      this.isNmiConnecting = value
    },

    updateCampaignEmailTemplate() {
      this.updateEmailTemplateTriggered++
    },

    updateShowAddProduct(showAddProduct, showRefresh) {
      this.showAddProduct = showAddProduct
      this.showRefresh = showRefresh
    },
    setInitDataUpdate(payload: any) {
      if (Object.keys(payload).includes('hasStripeConnected')) {
        this.hasStripeConnected = payload.hasStripeConnected
        this.updateStripeConnecting(false)
      } else if (Object.keys(payload).includes('hasAuthorizeNetConnected')) {
        this.hasAuthorizeNetConnected = payload.hasAuthorizeNetConnected
        this.updateAuthorizeNetConnecting(false)
      } else if (Object.keys(payload).includes('hasPaypalConnected')) {
        this.hasPaypalConnected = payload.hasPaypalConnected
        this.updatePaypalConnecting(false)
      } else if (Object.keys(payload).includes('hasNmiConnected')) {
        this.hasNmiConnected = payload.hasNmiConnected
        this.updateNmiConnecting(false)
      } else if (Object.keys(payload).includes('hasAffiliate')) {
        this.hasAffiliate = payload.hasAffiliate
      } else if (Object.keys(payload).includes('hasCampaign')) {
        this.hasCampaign = payload.hasCampaign
      } else if (Object.keys(payload).includes('skipOnboarding')) {
        this.skipOnboarding = payload.skipOnboarding
      } else if (Object.keys(payload).includes('hasLaunchedProgram')) {
        this.hasLaunchedProgram = payload.hasLaunchedProgram
      }
    },

    updateFullScreen(val: boolean) {
      this.showFullScreen = val
    },
    updateCampaignCount(val: number) {
      this.campaignCount = val
    },

    getPreviewUrl() {
      if (this.appHandshake) {
        this.appHandshake.then(parent => parent.emit('getPreviewUrl'))
      }
    },
    setPreviewUrl(preview) {
      this.builderPreviewDomain = preview
    },
  },
})
